// The access map that backs the Permissions page.
//
// One place defines which sites exist and what sections each has, so the admin screen,
// the portal's own menu and any future enforcement all agree. Section keys are stored on
// the user record and must stay stable once in use -- rename a label freely, never a key.

export type AccessLevel = 'none' | 'read' | 'write';

export interface SiteSection { key: string; label: string }

export interface SiteDefinition {
  key: string;
  label: string;
  url: string;
  /** The portal is the only site this build enforces; the others store their grants
   *  centrally, ready for each site to read. */
  enforced: boolean;
  sections: SiteSection[];
}

export const SITES: SiteDefinition[] = [
  {
    key: 'portal',
    label: 'Shipment Portal',
    url: 'https://portal.limaautomotive.com',
    enforced: true,
    sections: [
      { key: 'dashboard', label: 'Dashboard (old, hidden)' },
      { key: 'dashboard-v2', label: 'Dashboard' },
      { key: 'updates', label: 'Updates' },
      { key: 'tasks', label: 'Tasks' },
      { key: 'users', label: 'Users' },
      { key: 'email-notifications', label: 'Email Notifications' },
      { key: 'email-builder', label: 'Email Builder' },
      { key: 'email-preview', label: 'Email Preview' },
      { key: 'ports', label: 'Ports' },
      { key: 'calculator', label: 'Calculator' },
      { key: 'ticket-hub', label: 'Ticket Hub' },
      { key: 'permissions', label: 'Permissions' },
      { key: 'limasign', label: 'LIMAsign' },
    ],
  },
  {
    key: 'crm',
    label: 'Quotes',
    url: 'https://sales.limaautomotive.com/quotes',
    enforced: true,
    sections: [
      { key: 'quotes', label: 'Quotes list' },
      { key: 'invoices', label: 'Quote / invoice editor' },
      { key: 'custom-quotes', label: 'Custom quotes' },
      { key: 'clients', label: 'Contacts' },
      { key: 'analytics', label: 'Analytics' },
    ],
  },
  {
    key: 'accounting',
    label: 'Accounting',
    url: 'https://accounting.limaautomotive.com',
    enforced: true,
    sections: [
      { key: 'dashboard', label: 'Dashboard' },
      { key: 'shipments', label: 'Shipments' },
      { key: 'expenses', label: 'Expenses' },
      { key: 'vendors', label: 'Vendors' },
      { key: 'reports', label: 'Reports' },
    ],
  },
  {
    key: 'leads',
    label: 'Leads',
    url: 'https://sales.limaautomotive.com',
    enforced: true,
    sections: [
      { key: 'dashboard', label: 'Dashboard' },
      { key: 'leads', label: 'Leads (list and board)' },
      { key: 'campaigns', label: 'Email Campaigns' },
      { key: 'settings', label: 'Settings (tags and stages)' },
    ],
  },
  {
    key: 'api',
    label: 'Pricing API',
    url: 'https://limaapi.onrender.com',
    enforced: true,
    sections: [
      { key: 'rates', label: 'Fees, customs and tariffs' },
      { key: 'ports', label: 'Ports' },
      { key: 'regions', label: 'Regions (new countries and their prices)' },
      { key: 'audit', label: 'Audit log' },
      { key: 'users', label: 'API accounts' },
    ],
  },
  {
    // The public website's blog admin (limaautomotive.com/blog/admin). Read = see posts and drafts,
    // write = create, edit, publish and delete.
    key: 'website',
    label: 'Website blog',
    url: 'https://limaautomotive.com/blog/admin',
    enforced: true,
    sections: [
      { key: 'posts', label: 'Blog posts' },
    ],
  },
  {
    // The stand-alone Ticket Hub site. Until its own address is set up it lives at
    // https://limatickethub.netlify.app; the portal's Ticket Hub page stays as it is.
    key: 'tickethub',
    label: 'Ticket Hub (own site)',
    url: 'https://tickethub.limaautomotive.com',
    enforced: true,
    sections: [
      { key: 'tickets', label: 'Tickets' },
    ],
  },
];

/** user.sitePermissions shape: { [siteKey]: { [sectionKey]: AccessLevel } } */
export type SitePermissions = Record<string, Record<string, AccessLevel>>;

export const emptyPermissions = (): SitePermissions =>
  Object.fromEntries(
    SITES.map((site) => [
      site.key,
      Object.fromEntries(site.sections.map((s) => [s.key, 'none' as AccessLevel])),
    ]),
  );

/**
 * Whether an admin has ever set this person's access for a site.
 *
 * The Permissions page saves the whole grid, so a person edited there has an
 * entry for the site even when every section is none -- a deliberate "no
 * access". Only someone never set up at all keeps their old role-based access,
 * so switching this on cannot lock out existing staff.
 */
export const hasSiteEntry = (
  user: { sitePermissions?: SitePermissions } | null | undefined,
  siteKey: string,
): boolean =>
  Boolean(user?.sitePermissions && Object.prototype.hasOwnProperty.call(user.sitePermissions, siteKey));

/** Admins are never gated; everyone else needs an explicit grant. */
export const accessLevel = (
  user: { role?: string; sitePermissions?: SitePermissions } | null | undefined,
  siteKey: string,
  sectionKey: string,
): AccessLevel => {
  if (!user) return 'none';
  if (user.role === 'admin') return 'write';
  if (siteKey === 'portal' && TASK_KEYS.includes(sectionKey)) return taskLevel(user.sitePermissions);
  return user.sitePermissions?.[siteKey]?.[sectionKey] || 'none';
};

/*
 * Tasks and Tasks V2 used to be two rows for the same tasks. There is one Tasks
 * page and one Tasks row now. Older grants sit under either key, so the higher of
 * the two counts, and saving the Tasks row writes both keys so they stay in step.
 * The server applies the same rule (utils/auth.js).
 */
const TASK_KEYS = ['tasks', 'tasks-v2'];
const RANK: Record<AccessLevel, number> = { none: 0, read: 1, write: 2 };
export const taskLevel = (perms: SitePermissions | undefined): AccessLevel =>
  TASK_KEYS.map((k) => (perms?.portal?.[k] || 'none') as AccessLevel).reduce((a, b) => (RANK[b] > RANK[a] ? b : a), 'none' as AccessLevel);

/** Show the combined Tasks grant on the Tasks row, and save it under both keys. */
export const withTaskGrant = (perms: SitePermissions): SitePermissions => {
  if (!perms?.portal) return perms;
  const level = perms.portal.tasks || 'none';
  return { ...perms, portal: { ...perms.portal, tasks: level, 'tasks-v2': level } };
};
export const readTaskGrant = (perms: SitePermissions): SitePermissions =>
  perms?.portal ? { ...perms, portal: { ...perms.portal, tasks: taskLevel(perms) } } : perms;

/**
 * Sections that hand over the keys -- Permissions and Ports.
 *
 * These need a grant that was actually ticked. The "never set up keeps their old
 * access" rule does not apply, so nobody reaches them by never having been set up.
 */
export const SENSITIVE_SECTIONS = ['permissions', 'ports'];

/** An explicit grant only: admins pass, everyone else needs the section ticked. */
export const hasGrant = (
  user: { role?: string; sitePermissions?: SitePermissions } | null | undefined,
  siteKey: string,
  sectionKey: string,
  level: 'read' | 'write' = 'read',
): boolean => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  const granted = user.sitePermissions?.[siteKey]?.[sectionKey] || 'none';
  return level === 'write' ? granted === 'write' : granted !== 'none';
};

export const canRead = (user: any, siteKey: string, sectionKey: string) =>
  accessLevel(user, siteKey, sectionKey) !== 'none';

export const canWrite = (user: any, siteKey: string, sectionKey: string) =>
  accessLevel(user, siteKey, sectionKey) === 'write';

/**
 * Where each portal section lives, and the first one this person may open.
 *
 * Used to send someone to a page they can actually use instead of showing them a
 * "no access" screen for one they cannot.
 */
export const PORTAL_PATHS: Record<string, string> = {
  dashboard: '/admin/dashboard-legacy',
  'dashboard-v2': '/admin',
  updates: '/admin/updates',
  tasks: '/admin/tasks',
  'tasks-v2': '/admin/tasks',
  users: '/admin/users',
  'email-notifications': '/admin/email',
  'email-builder': '/admin/email-builder',
  'email-preview': '/admin/email-preview',
  ports: '/admin/ports',
  calculator: '/admin/calculator',
  'ticket-hub': '/admin/ticket-hub',
  permissions: '/admin/permissions',
  limasign: '/admin/limasign',
};

export const firstAllowedPortalPath = (
  user: { role?: string; sitePermissions?: SitePermissions } | null | undefined,
): string | null => {
  if (!user) return null;
  if (user.role === 'admin') return '/admin';
  const section = SITES.find((s) => s.key === 'portal')?.sections
    .find((s) => (SENSITIVE_SECTIONS.includes(s.key)
      ? hasGrant(user, 'portal', s.key)
      : canRead(user, 'portal', s.key)));
  return section ? PORTAL_PATHS[section.key] || null : null;
};

/** Tracking-only roles: a client sees their own shipments, a dealer sees theirs plus
 *  any client shipments assigned to them. Neither gets the section matrix. */
export const isTrackingOnlyRole = (role?: string) => role === 'client' || role === 'dealer';
